import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { getCookies } from '@pkg-services/cookies/Cookies';
import { ApiCallService } from '@pkg-services/callApi/api-call.service';
import { AdminActionCenterService } from '@pkg-services/admin/admin-action-center.service';
import { RouteAdminDashboard } from '@pkg-framework/models/admin';
import type { AdminActionItem, DashboardData } from '@pkg-framework/models/admin';
import { IconsDirective } from '@pkg-directives/icons/icons.directive';
import { AdminActionListComponent } from '@app/shared/components/admin-action-list/admin-action-list.component';
import { UpcomingCallbacksComponent } from '@app/shared/components/upcoming-callbacks/upcoming-callbacks.component';

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [CommonModule, RouterLink, FormsModule, IconsDirective, AdminActionListComponent, UpcomingCallbacksComponent],
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.scss'
})
export class DashboardComponent implements OnInit {
  isLoading = true;
  dashboard: DashboardData | null = null;
  actionItems: AdminActionItem[] = [];
  fromDate = '';
  toDate = '';

  constructor(
    private api: ApiCallService,
    private actionCenter: AdminActionCenterService
  ) {}

  /** « Prénom / nom » du compte (le sous-titre est sa fonction, pas une partie du nom). */
  get userName(): string {
    return (getCookies('user_name') || '').trim() || 'Admin';
  }

  get stats(): DashboardStatCard[] {
    const d = this.dashboard;
    const formsBasePath = '/admin/demandes/ajout-activites';
    if (!d) return [];
    return [
      {
        label: 'Organisations',
        value: String(d.organizations_total),
        sub: 'Total plateforme',
        link: '/admin/organisations',
        tone: 'neutral'
      },
      {
        label: 'Inscriptions en attente',
        value: String(d.organizations_pending_review ?? 0),
        sub: 'À modérer',
        link: '/admin/onboarding/inscriptions',
        tone: (d.organizations_pending_review ?? 0) > 0 ? 'warn' : 'neutral'
      },
      {
        label: 'Justificatifs Peekship',
        value: String(d.peekship_documents_pending ?? 0),
        sub: 'Dossiers à certifier',
        link: '/admin/peekship/partenaires',
        queryParams: { tab: 'documents' },
        tone: (d.peekship_documents_pending ?? 0) > 0 ? 'warn' : 'neutral'
      },
      {
        label: 'Demandes d’activité',
        value: String(d.activity_add_requests_pending ?? 0),
        sub: 'En attente',
        link: formsBasePath,
        queryParams: { form_type: 'activity_add_request', status: 'pending' },
        tone: (d.activity_add_requests_pending ?? 0) > 0 ? 'warn' : 'neutral'
      },
      {
        label: 'Messages contact',
        value: String(d.contact_messages_pending ?? 0),
        sub: 'En attente',
        link: formsBasePath,
        queryParams: { form_type: 'contact', status: 'pending' },
        tone: (d.contact_messages_pending ?? 0) > 0 ? 'warn' : 'neutral'
      },
      {
        label: 'Alertes paiements',
        value: String((d.payments_audit?.critical_issues ?? 0) + (d.payments_audit?.warn_issues ?? 0)),
        sub: 'Critical + warn (30j)',
        link: '/admin/paiements/sante',
        tone: (d.payments_audit?.critical_issues ?? 0) > 0 ? 'critical' : 'neutral'
      },
      {
        label: 'Transactions (période)',
        value: String(d.transactions_count),
        sub: this.periodHumanLabel,
        link: '/admin/paiements/transactions',
        tone: 'neutral'
      },
      {
        label: 'CA TTC (période)',
        value: formatEuro(d.transactions_revenue_ttc),
        sub: this.periodHumanLabel,
        link: '/admin/paiements/transactions',
        tone: 'neutral'
      },
      {
        label: 'Commissions plateforme',
        value: formatEuro(d.platform_commissions),
        sub: this.periodHumanLabel,
        link: '/admin/paiements/transactions',
        tone: 'neutral'
      }
    ];
  }

  get periodHumanLabel(): string {
    const d = this.dashboard;
    if (!d?.period?.from || !d?.period?.to) return 'Période';
    return `${d.period.from} → ${d.period.to}`;
  }

  ngOnInit(): void {
    const to = new Date();
    const from = new Date(to.getFullYear(), to.getMonth(), 1);
    this.fromDate = from.toISOString().slice(0, 10);
    this.toDate = to.toISOString().slice(0, 10);
    this.load();
  }

  load(): void {
    this.isLoading = true;
    this.api.get(`${RouteAdminDashboard}?from_date=${this.fromDate}&to_date=${this.toDate}`).subscribe({
      next: (res: { data: DashboardData }) => {
        this.dashboard = res.data;
        this.actionItems = res.data?.action_center?.items ?? [];
        this.actionCenter.setFromDashboard(res.data?.action_center);
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
      }
    });
  }
}

function formatEuro(n: number): string {
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(n);
}

export interface DashboardStatCard {
  label: string;
  value: string;
  sub: string;
  link: string;
  queryParams?: Record<string, string>;
  tone?: 'neutral' | 'warn' | 'critical';
}
